import { useState, useMemo } from "react";
import { Play, ChevronDown, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { VideoModal } from "../components/VideoModal";
import { PhotoGallery } from "../components/PhotoGallery";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { PORTFOLIO } from "../data/site";

const CATEGORIES = ["All", ...Array.from(new Set(PORTFOLIO.map((p) => p.category)))];

export default function Portfolio() {
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("All");

  const items = useMemo(
    () => (filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter)),
    [filter]
  );

  const openVideo = (p) => {
    if (p.status) return; // in-progress: not clickable
    if (p.link) {
      window.open(p.link, "_blank", "noopener");
      return;
    }
    if (!p.youtubeId && !p.vimeoId) return; // no media to play
    setActive(p);
    setOpen(true);
  };

  return (
    <div data-testid="portfolio-page">
      <section className="mx-auto max-w-7xl px-6 pb-14 pt-40 lg:px-10 lg:pt-48">
        <Reveal>
          <p className="overline text-sand-700">Portfolio / Showreel</p>
          <h1 className="mt-6 font-display text-6xl leading-[0.9] tracking-tight text-sand-100 md:text-8xl">
            The <span className="italic text-sand-700">archive</span>
          </h1>
        </Reveal>

        <Reveal className="mt-12 flex items-center justify-between border-t border-border pt-8">
          <span className="overline text-sand-400">{items.length} films</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 border border-border px-5 py-3 overline text-sand-100 transition-colors hover:border-sand-700" data-testid="filter-trigger">
                {filter} <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-border bg-surface text-sand-100" data-testid="filter-menu">
              {CATEGORIES.map((c) => (
                <DropdownMenuItem key={c} onClick={() => setFilter(c)} className="cursor-pointer focus:bg-[#1d1815] focus:text-sand-700" data-testid={`filter-option-${c.toLowerCase()}`}>
                  {c}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-32 lg:px-10">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p, i) => (
            <motion.button
              key={p.id}
              onClick={() => openVideo(p)}
              data-testid={`portfolio-card-${p.id}`}
              className="group relative block overflow-hidden text-left"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img src={p.thumb} alt={p.title} loading="lazy" className={`h-full w-full object-cover transition-all duration-700 group-hover:scale-105 ${p.status ? "grayscale" : "grayscale-[30%] group-hover:grayscale-0"}`} />
              </div>
              {p.status ? (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0D0B0A]/55">
                  <span className="border border-sand-100 px-6 py-3 font-body text-sm uppercase tracking-[0.28em] text-sand-100" data-testid={`status-${p.id}`}>
                    {p.status}
                  </span>
                </div>
              ) : (p.link || p.youtubeId || p.vimeoId) ? (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0D0B0A]/30 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <span className="flex h-20 w-20 items-center justify-center rounded-full border border-sand-100 text-sand-100">
                    {p.link ? <ArrowUpRight size={24} /> : <Play size={22} fill="currentColor" />}
                  </span>
                </div>
              ) : null}
              <div className="mt-5 flex items-center justify-between">
                <span className="font-display text-3xl text-sand-100">{p.title}</span>
                <span className="overline text-sand-400">{p.year}</span>
              </div>
              <span className="mt-1 block overline text-sand-700">{p.category}</span>
            </motion.button>
          ))}
        </div>
      </section>

      <PhotoGallery />

      <VideoModal open={open} onOpenChange={setOpen} video={active} />
    </div>
  );
}
